import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { observer } from "mobx-react-lite";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { LogViewModel } from "../../features/log/log-view-model";
import { RegistrationChange } from "../../features/registrations/registrations-service";
import { RegistrationsViewModel } from "../../features/registrations/registrations-viewmodel";
import { UserIdViewModel } from "../../features/user-id/user-id-viewmodel";
import { AppRoutes } from "../../routes/routes";
import { formatShortTimestamp, maskUuid } from "../../utils/formatters";
import { useViewModel } from "../../viewmodel/use-view-model";
import { WriteAccessAction } from "../access/role-access";
import { bsClassForRegistrationStatus } from "./reg-bs-mapping";
import { RegistrationDetailChangeModalView } from "./registration-detail-change-modal-view";
export const RegistrationsListView = observer(() => {
    const vm = useViewModel(RegistrationsViewModel);
    const logVM = useViewModel(LogViewModel);
    const userVM = useViewModel(UserIdViewModel);
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [deleteRegId, setDeleteRegId] = useState("");
    const handleDetailClick = (reg) => {
        vm.setSelectedRegistration(reg);
        navigate(AppRoutes.RegistrationsDetail.replace(":uuid", reg.registrationId ?? ""));
    };
    const handleDeleteClick = () => {
        if (deleteRegId === "") {
            logVM.logAppError(t("registrations.error.emptyId"));
            return;
        }
        void vm.deleteRegistration(deleteRegId, userVM.userId);
    };
    const handleLoadMoreClick = () => {
        void vm.getNextPage(userVM.userId);
    };
    const handleCopyClick = (success) => {
        if (success) {
            logVM.logAppSuccess("Value copied successfully", "copyToClipboard.success");
        }
        else {
            logVM.logAppError("Value copy failed", "copyToClipboard.error");
        }
    };
    if (vm.registrations.length === 0) {
        return (_jsxs("div", { className: "card-body", children: [_jsx("h5", { children: t("registrations.error.nothingFound", { userId: userVM.userId }) }), _jsx(Link, { to: "/", children: t("registrations.tryDifferentUser") })] }));
    }
    return (_jsxs(_Fragment, { children: [_jsx("div", { className: "card-body", children: _jsx("div", { className: "card-text", children: _jsx("div", { children: _jsxs("table", { className: "table table-hover table-borderless", children: [_jsx("thead", { children: _jsxs("tr", { className: "align-middle", children: [_jsx("th", { children: t("registrations.detail.created") }), _jsx("th", { children: t("registrations.detail.id") }), _jsx("th", { children: t("registrations.detail.status") }), _jsx("th", { children: t("registrations.detail.deviceInfo") }), _jsx("th", { children: t("registrations.detail.application") }), _jsx("th", { children: t("registrations.detail.platform") }), _jsx("th", {})] }) }), _jsx("tbody", { children: vm.registrations.map((reg) => renderRegistrationRow(reg, handleDetailClick, handleCopyClick, setDeleteRegId, t)) })] }) }) }) }), _jsxs("div", { className: "card-footer pt-3 pb-3 align-items-center", children: [vm.allRegistrationsLoaded &&
                        _jsx("span", { className: "text-muted", children: t("registrations.allEntriesLoaded") }), !vm.allRegistrationsLoaded &&
                        _jsx("button", { type: "button", className: "btn btn-secondary btn-sm", onClick: handleLoadMoreClick, children: t("registrations.loadMore") })] }), _jsx(RegistrationDetailChangeModalView, { modalId: "registrationDeleteModal", changeType: RegistrationChange.remove, onConfirm: () => handleDeleteClick() })] }));
});
function renderRegistrationRow(reg, detailHandleClick, handleCopyClick, setDeleteRegId, t) {
    return (_jsxs("tr", { className: "align-middle", style: { cursor: "pointer" }, onClick: () => detailHandleClick(reg), children: [_jsx("td", { className: "pt-3 pb-3", children: formatShortTimestamp(reg.timestampCreated) }), _jsxs("td", { className: "font-monospace pt-3 pb-3", children: [maskUuid(reg.registrationId), _jsx("i", { className: "bi bi-clipboard", onClick: async (evt) => {
                            evt.stopPropagation();
                            evt.preventDefault();
                            if (reg.registrationId) {
                                copyToClipboard(reg.registrationId, handleCopyClick);
                            }
                        } })] }), _jsx("td", { className: "pt-3 pb-3", children: _jsx("span", { className: `badge ${bsClassForRegistrationStatus(reg)}`, children: t(`registrations.status.${reg.registrationStatus}`) }) }), _jsx("td", { className: "pt-3 pb-3", children: reg.deviceInfo }), _jsx("td", { className: "pt-3 pb-3", children: reg.applicationId }), _jsx("td", { className: "pt-3 pb-3", children: reg.platform }), _jsx("td", { className: "pt-3 pb-3", children: reg.registrationStatus !== "REMOVED" &&
                    _jsx(WriteAccessAction, { children: _jsx("button", { type: "button", "data-bs-toggle": "modal", "data-bs-target": "#registrationDeleteModal", className: "btn btn-outline btn-sm", onClick: (evt) => {
                                setDeleteRegId(reg.registrationId ?? "");
                                evt.preventDefault();
                                evt.stopPropagation();
                            }, children: _jsx("img", { src: "assets/ic-trash.svg", alt: "Delete icon" }) }) }) })] }, reg.registrationId));
}
export function copyToClipboard(copy, handleCopyResult) {
    navigator.clipboard.writeText(copy).then(() => {
        handleCopyResult(true);
    }).catch(() => {
        handleCopyResult(false);
    });
}
